import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import IdleTimer from "react-idle-timer";
import { logoutAct, validateJwtTokenExpiry } from "../../Actions/authActions";
import TimeoutModal from "../../Pages/Dashboard/TimeoutPopup";
import config from "../../config";
import {
  eventUrl,
  subDomain,
  saveLogRocketSession,
} from "../../utils";
import { executeGet } from "../../Api";

let logoutTimer = null;

function ProtectedAdminRoute({ children }) {

  const isAuthenticated = useSelector(
    (state) => state.auth.isAuthenticated
  );
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const dispatch = useDispatch();
  const idleTimer = useRef();
  const subdomain = subDomain();
  const [showModal, setShowModal] = useState(false);
  const [isVerifiedAdmin, setIsVerifiedAdmin] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  const MINUTE_MS = 60000;//0;

  // Backend verification effect
  useEffect(() => {
    const verifyAdminStatus = async () => {
      const currentSubdomain = subDomain();

      // Check 1: user exists
      if (!user || Object.keys(user).length === 0) {
        setIsVerifiedAdmin(false);
        setIsVerifying(false);
        return;
      }

      // Check 2: user claims to be admin
      if (user.type !== 'admin') {
        setIsVerifiedAdmin(false);
        setIsVerifying(false);
        return;
      }

      // Check 3: subdomain matches
      if (user.subdomain && user.subdomain !== currentSubdomain && currentSubdomain) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setIsVerifiedAdmin(false);
        setIsVerifying(false);
        return;
      }

      // Check 4: verify with backend
      try {
        const response = await executeGet('auth/verify-admin');
        const data = response?.data || response;

        if (data && data.isAdmin === true) {
          if (data.subdomain !== currentSubdomain && currentSubdomain) {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setIsVerifiedAdmin(false);
          } else {
            setIsVerifiedAdmin(true);
          }
        } else {
          setIsVerifiedAdmin(false);
        }
      } catch (error) {
        console.error('Admin verification failed:', error?.message);
        saveLogRocketSession({ eventType: "admin_verification_error", metadata: { error: error?.message } });
        // Don't allow access if backend verification fails
        setIsVerifiedAdmin(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAdminStatus();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(validateJwtTokenExpiry(subdomain));//code runs every 10 minutes
    }, MINUTE_MS);
    return () => clearInterval(interval); // This represents the unmount function, in which you need to clear our interval to prevent memory leaks.
  }, [])


  const onIdleFunc = () => {
    setShowModal(!showModal);
    logoutTimer = setTimeout(() => {
      dispatch(logoutAct(true, subdomain));
    }, 170400000);
  };

  const handleStayLoggedIn = () => {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      logoutTimer = null;
    }
    idleTimer.current.reset();
    setShowModal(!showModal);
  };

  const redirect = () => {
    window.location.href = `${eventUrl(subdomain)}admin`;
  }

  // Show loading while verifying
  if (isVerifying) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Verifying access...</p>
      </div>
    );
  }

  // Redirect if not verified
  if (!isVerifiedAdmin) {
    redirect();
    return null;
  }

  return (
    <>
      <IdleTimer
        ref={idleTimer}
        element={document}
        stopOnIdle={true}
        onIdle={onIdleFunc}
        timeout={parseInt(config.USER_INACTIVE)}
      />
      <TimeoutModal
        openModal={showModal}
        onCloseModal={setShowModal}
        handleStayLoggedIn={handleStayLoggedIn}
      />

      {children}

    </>
  );
}

export default ProtectedAdminRoute;
